import { useQuery } from "@tanstack/react-query";

export interface LosBucketData {
  label: string;
  min: number;
  max: number | null;
  count: number;
}

export interface LosGroupData {
  buckets: LosBucketData[];
  averageDays: number;
  medianDays: number;
  totalAnimals: number;
}

export interface LengthOfStayResponse {
  outcomed: {
    all: LosGroupData;
    bySpecies?: Record<string, LosGroupData>;
  };
  currentInShelter: {
    all: LosGroupData;
    bySpecies?: Record<string, LosGroupData>;
  };
}

export interface IntakeOutcomePeriod {
  periodStart: string;
  periodEnd: string;
  intakes: number;
  outcomes: number;
  netChange: number;
  onHoldCount: number;
  bySpecies?: Record<string, { intakes: number; outcomes: number; netChange: number; onHoldCount: number }>;
}

export interface IntakeOutcomeTotals {
  totalIntakes: number;
  totalOutcomes: number;
  netChange: number;
  currentOnHold: number;
  bySpecies?: Record<string, { totalIntakes: number; totalOutcomes: number; netChange: number; currentOnHold: number }>;
}

export interface IntakeOutcomeTrendsResponse {
  granularity: "daily" | "weekly";
  series: IntakeOutcomePeriod[];
  totals: IntakeOutcomeTotals;
}

export interface OnHoldResponse {
  total: number;
  bySpecies: Record<string, number>;
  byLocation: Record<string, Record<string, number>>;
}

export interface AverageStaysSpecies {
  averageStays: number;
  totalAnimals: number;
  totalStays: number;
}

export interface AverageStaysResponse {
  overall: AverageStaysSpecies;
  bySpecies: Record<string, AverageStaysSpecies>;
}

export interface OutcomeReasonEntry {
  reason: string;
  count: number;
  percentage: number;
}

export interface OutcomeReasonResponse {
  total: number;
  reasons: OutcomeReasonEntry[];
  bySpecies?: Record<string, { total: number; reasons: OutcomeReasonEntry[] }>;
}

export function useLengthOfStay(days: number, location?: string, species?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  const url = `/api/population/length-of-stay?${params}`;

  return useQuery<LengthOfStayResponse>({
    queryKey: ["/api/population/length-of-stay", { days, location, species }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch length of stay data");
      return res.json();
    },
  });
}

export function useIntakeOutcomeTrends(days: number, location?: string, granularity: "daily" | "weekly" = "weekly", species?: string) {
  const params = new URLSearchParams({ days: String(days), granularity });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  const url = `/api/population/intake-outcome-trends?${params}`;

  return useQuery<IntakeOutcomeTrendsResponse>({
    queryKey: ["/api/population/intake-outcome-trends", { days, location, granularity, species }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch intake-outcome trends");
      return res.json();
    },
  });
}

export function useOnHold(location?: string) {
  const params = new URLSearchParams();
  if (location) params.set("location", location);
  const url = `/api/population/on-hold${params.toString() ? `?${params}` : ""}`;

  return useQuery<OnHoldResponse>({
    queryKey: ["/api/population/on-hold", { location }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch on-hold data");
      return res.json();
    },
  });
}

export function useAverageStays(days: number, location?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  const url = `/api/population/average-stays?${params}`;

  return useQuery<AverageStaysResponse>({
    queryKey: ["/api/population/average-stays", { days, location }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch average stays data");
      return res.json();
    },
  });
}

export function useOutcomeReasons(days: number, location?: string, species?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  const url = `/api/population/outcome-reasons?${params}`;

  return useQuery<OutcomeReasonResponse>({
    queryKey: ["/api/population/outcome-reasons", { days, location, species }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch outcome reason data");
      return res.json();
    },
  });
}

export interface AdoptionLevelCounts {
  L1: number;
  L2: number;
  L3: number;
  L4: number;
  unknown: number;
}

export interface AdoptionPipelinePoint extends AdoptionLevelCounts {
  date: string;
  bySpecies: Record<string, AdoptionLevelCounts>;
}

export interface AdoptionPipelineResponse {
  series: AdoptionPipelinePoint[];
  latest: (AdoptionLevelCounts & { total: number; bySpecies: Record<string, AdoptionLevelCounts> }) | null;
}

export function useAdoptionPipeline(days: number, location?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  const url = `/api/population/adoption-pipeline?${params}`;

  return useQuery<AdoptionPipelineResponse>({
    queryKey: ["/api/population/adoption-pipeline", { days, location }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch adoption pipeline data");
      return res.json();
    },
  });
}

export interface KennelStatusCounts {
  available: number;
  unavail: number;
  evaluation: number;
  strayWait: number;
  unknown: number;
}

export interface KennelStatusPoint extends KennelStatusCounts {
  date: string;
  bySpecies: Record<string, KennelStatusCounts>;
}

export interface KennelStatusTrendsResponse {
  series: KennelStatusPoint[];
  latest: (KennelStatusCounts & { total: number; bySpecies: Record<string, KennelStatusCounts> }) | null;
}

export function useKennelStatusTrends(days: number, location?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  const url = `/api/population/kennel-status-trends?${params}`;

  return useQuery<KennelStatusTrendsResponse>({
    queryKey: ["/api/population/kennel-status-trends", { days, location }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch kennel status trends");
      return res.json();
    },
  });
}

export interface SubstatusBreakdownResponse {
  current: Record<string, number> & { total: number; bySpecies: Record<string, Record<string, number> & { total: number }> };
  series: (Record<string, any> & { date: string; reportId: string; bySpecies: Record<string, Record<string, number>> })[];
}

export type AvailabilityStatus = "available" | "unavailable";

export function useSubstatusBreakdown(days: number, location?: string, species?: string, status: AvailabilityStatus = "unavailable") {
  const params = new URLSearchParams({ days: String(days), status });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  const url = `/api/population/substatus-breakdown?${params}`;

  return useQuery<SubstatusBreakdownResponse>({
    queryKey: ["/api/population/substatus-breakdown", { days, location, species, status }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch substatus breakdown");
      return res.json();
    },
  });
}

export interface EvalBreakdownResponse {
  current: Record<string, number> & { total: number; bySpecies: Record<string, Record<string, number> & { total: number }> };
  series: (Record<string, any> & { date: string; reportId: string; bySpecies: Record<string, Record<string, number>> })[];
}

export function useEvaluationBreakdown(days: number, location?: string, species?: string) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  const url = `/api/population/evaluation-breakdown?${params}`;

  return useQuery<EvalBreakdownResponse>({
    queryKey: ["/api/population/evaluation-breakdown", { days, location, species }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch evaluation breakdown");
      return res.json();
    },
  });
}

export interface IntakeSourceEntry {
  name: string;
  count: number;
  percentage: number;
}

export interface IntakeSourcesResponse {
  total: number;
  sources: IntakeSourceEntry[];
  bySpecies: Record<string, { total: number; sources: IntakeSourceEntry[] }>;
}

export function useIntakeSources(days: number, location?: string, species?: string, limit?: number) {
  const params = new URLSearchParams({ days: String(days) });
  if (location) params.set("location", location);
  if (species) params.set("species", species);
  if (limit) params.set("limit", String(limit));
  const url = `/api/population/intake-sources?${params}`;

  return useQuery<IntakeSourcesResponse>({
    queryKey: ["/api/population/intake-sources", { days, location, species, limit }],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch intake sources");
      return res.json();
    },
  });
}
